import {Injectable, inject} from '@angular/core';
import {jsPDF} from 'jspdf';
import autoTable from 'jspdf-autotable';
import {formatDate} from "@angular/common";
import {PdfFontsService} from "./pdf-fonts.service";
import {RevenuePdfData} from "../revenue/interfaces/revenuePdfData.interface";

@Injectable({
  providedIn: 'root',
})
export class PdfService {
  private pdfFontsService = inject(PdfFontsService)
  private baseFontSize: number = 10;
  private baseTextColor: number = 100;
  private doc: jsPDF = new jsPDF({format: 'a4', compress: true});

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  async revenuePdf(
    data: RevenuePdfData,
    imgData: string,
    chartHeight: number,
    chartWidth: number,
  ): Promise<void> {

    const doc = new jsPDF({ format: 'a4', compress: true });

    const pageWidth: number = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();

    const margin = 14;
    const fontSize = 10;

    const logo = await this.loadImage('assets/images/logo.png');


    doc.addImage(logo, 'png', pageWidth - 40, 10, 28, 40);

    doc.setTextColor(40);
    doc.setFontSize(fontSize * 2);
    doc.text(data.title, margin, 40);
    doc.setFontSize(fontSize * 1.2);
    doc.text(`IDF ${data.idf} (${data.name})`, margin, 47);

    const maxWidth = pageWidth - 2 * margin;
    const maxHeight = 100;
    const scale = Math.min(maxWidth / chartWidth, maxHeight / chartHeight);
    const imgW = chartWidth * scale;
    const imgH = chartHeight * scale;
    const imgY = 52;
    const narrow = data.cols[0].length <= 2;

    doc.addImage(imgData, 'PNG', (pageWidth - imgW) / 2, imgY, imgW, imgH);

    autoTable(doc, {
      head: data.cols,
      headStyles: {fillColor: [237, 28, 36]},
      bodyStyles: { fontSize: fontSize * 0.8 },
      styles: { halign: 'right' },
      body: data.rows,
      tableWidth: narrow ? 90 : 'auto',
      startY: imgY + imgH + 10,
      showHead: 'firstPage',
    });

    doc.save(`${data.fileName}.pdf`);
  }

  exportToPdf(header: any[], rows: any[], filename: string, title: string, address?: any, data?: any) {
    this.doc = new jsPDF({compress: true, format: 'a4'});
    const pageWidth: number = this.doc.internal.pageSize.width || this.doc.internal.pageSize.getWidth();
    const pageHeight: number = this.doc.internal.pageSize.height || this.doc.internal.pageSize.getHeight();
    const noOfRows: number = rows.length;
    let footerY: number = 0;
    // logo

    const image: string = 'assets/images/logo.jpg';
    this.addImage(image, pageWidth);

    this.addHagedaAddress();
    // Pharmacy address
    if (address) {
      this.addPharmacyAddress(address);
    }
    // print date
    this.doc.setFontSize(this.baseFontSize);
    this.doc.setTextColor(this.baseTextColor);
    let date = new Date();
    if(data?.hasOwnProperty('date')){
      date = new Date(data['date']);
    }
    if (data?.hasOwnProperty('dateFormat')) {
      this.doc.text('Datum\n' + formatDate(date, data.dateFormat, 'de-DE'), pageWidth - 60, 80);
    } else {
      // this block is used by preorder archive as we are not passing any dateFormat
      this.doc.text(formatDate(date, 'mediumDate', 'de-DE'), pageWidth - 60, 80);
    }

    // title
    this.doc.setFontSize(this.baseFontSize * 2);
    this.doc.setTextColor(this.baseTextColor * 0.4);
    this.doc.text(title, 14, 95);
    // description

    let y = 105;
    if (data?.hasOwnProperty('desc')) {
      this.doc.setFontSize(this.baseFontSize);
      this.doc.setTextColor(this.baseTextColor);
      this.doc.text(data.desc, 14, y);
      y = 120;
    }
    // table
    let yPos = 0;
    autoTable(this.doc, {
      head: [header],
      headStyles: {fillColor: [237, 28, 36]},
      bodyStyles: {fontSize: this.baseFontSize * 0.8},
      body: rows,
      startY: y,
      showHead: 'firstPage',
      pageBreak: 'auto',
      didDrawPage: function(data) {
        yPos = (data.cursor?.y) ? data.cursor.y : 0;
      }
    });

    if(yPos > 0) {
      y = yPos + 18;
    } else{
      y = noOfRows*18 + y;
    }

    if(y > pageHeight - 30) {
      this.doc.addPage();
      const ycalc = y - pageHeight - 20
      footerY = (ycalc > 0) ? ycalc : 30;
    }

    let footY = 0;
    //calculate footerY
    if (data?.hasOwnProperty('footerTitle')) {
      if(footerY === 0) {
        const dimensions = this.doc.getTextDimensions(data.footerTitle);
        footY = pageHeight - 30 - dimensions.h;
      }
    }

    if (data?.hasOwnProperty('footerDesc')) {
      this.doc.setFontSize(this.baseFontSize * 1.5);
      const dimensions = this.doc.getTextDimensions(data.footerDesc);
      if(footerY === 0) {
        footY = (footY > 0) ? footY - dimensions.h : pageHeight - 30 - dimensions.h;
      }
    }

    if(footY > 0) {
      if(footY < y) {
        this.doc.addPage();
        footerY = 30;
      } else {
        footerY = footY;
      }
    }

    // footer description
    if (data?.hasOwnProperty('footerDesc')) {
      this.doc.setFontSize(this.baseFontSize);
      this.doc.setTextColor(this.baseTextColor);

      this.doc.text(data.footerDesc, 14, footerY + 10, {maxWidth: pageWidth - 28});
    }

    // footer Title
    if (data?.hasOwnProperty('footerTitle')) {
      this.doc.setTextColor(this.baseTextColor * 0.4);
      this.doc.setFontSize(this.baseFontSize * 1.5);
      this.doc.text(data.footerTitle, pageWidth / 2, footerY, {align: 'center'});
    }

    this.doc.save(`${filename}.pdf`);
  }

  addImage(image: string, pageWidth: number) {
    const img = new Image();
    img.src = 'assets/images/logo.png';
    this.doc.addImage(img, 'png', pageWidth - 40, 10, 28, 40);
  }

  addHagedaAddress() {
    // Phoenix headquarter address
    this.doc.setFontSize(this.baseFontSize * 0.8);
    this.doc.setTextColor(this.baseTextColor);
    this.doc.text(' Hageda-Stumpf Pharmahandel GmbH · 81379 München', 14, 50);
  }

  addPharmacyAddress(address: any) {
    this.doc.setFontSize(this.baseFontSize);
    this.doc.setTextColor(100);
    this.doc.text(address, 14, 60);
  }

  printA5Size(data: any) {
    const doc= new jsPDF({
      // orientation: 'landscape',
      unit: 'mm',
      // format: 'a5',
      compress: true
    });
    const baseFontSize = 10;
    const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
    const leftMargin = 10;
    this.pdfFontsService.addLatoFonts(doc);

    const img = new Image();
    img.src = 'assets/images/logo.jpg';
    doc.addImage(img, 'jpg', pageWidth - 30, 7, 18, 25);
    doc.setFont('Lato', 'normal');
    doc.setFontSize(baseFontSize * 1.5);
    doc.setTextColor(84,84,84);
    let y = 20;
    doc.text('Vereinbarung zu den Allgemeinen Retourenbedingungen (ARB)', leftMargin, y);

    doc.setFontSize(baseFontSize*0.9);

    y = y +7;
    doc.text('IDF ' + data.idf + ' (' + data.name + ')', leftMargin, y);

    y = y + 15;
    doc.setDrawColor(84,84,84);
    doc.line(leftMargin, y, doc.internal.pageSize.getWidth() - leftMargin, y);

    const subtitle1 = 'Rückgabefristen';
    const subtitle2 = 'Mindestwert / Gebühren';

    y = y + 15;
    doc.text(subtitle1, leftMargin, y);
    doc.setDrawColor(84, 84, 84);
    doc.line(leftMargin, y + 1, leftMargin + doc.getTextWidth(subtitle1), y + 1);
    doc.text(subtitle2, leftMargin + 110, y);
    doc.setDrawColor(84, 84, 84);
    doc.line(leftMargin +110, y + 1, leftMargin + 110 + doc.getTextWidth(subtitle2), y + 1);

    y = y + 10;

    const leftColumnX = 25;
    const rightColumnX = 136;
    doc.setFont('Lato', 'bold');
    doc.text(`${data.returnDeadlineOrderError} AT`, leftColumnX, y, { align: 'right' });
    doc.setFont('Lato', 'normal');
    doc.text('Rückgabefrist bei Bestellfehler', leftColumnX + 5, y);
    doc.setFont('Lato', 'bold');
    doc.text(`${data.fixedFeePerPacket.toFixed(2).replace('.', ',')} €`, rightColumnX, y, { align: 'right' });
    doc.setFont('Lato', 'normal');
    doc.text('Fixgebühr pro Packung', rightColumnX + 5, y);

    y = y+7;
    doc.setFont('Lato', 'bold');
    doc.text(`${data.returnDeadlineExpiry} AT`, leftColumnX, y, { align: 'right' });
    doc.setFont('Lato', 'normal');
    doc.text('Rückgabefrist bei Verfall (6 bis 12 Monate)', leftColumnX + 5, y);
    doc.setFont('Lato', 'bold');
    doc.text(`${data.fixedFeeHighPrice.toFixed(2).replace('.', ',')} €`, rightColumnX, y, { align: 'right' });
    doc.setFont('Lato', 'normal');
    doc.text('Fixgebühr für Hochpreisartikel', rightColumnX + 5, y);

    y = y+7;
    doc.setFont('Lato', 'bold');
    doc.text(`${data.returnDeadlineBtmCool} AT`, leftColumnX, y, { align: 'right' });
    doc.setFont('Lato', 'normal');
    doc.text('Rückgabefrist BTM & Kühlartikel', leftColumnX + 5, y);
    doc.setFont('Lato', 'bold');
    doc.text(`${data.minimumValueBtmCool.toFixed(2).replace('.', ',')} €`, rightColumnX, y, { align: 'right' });
    doc.setFont('Lato', 'normal');
    doc.text('Mindestwert BTM & Kühlartikel', rightColumnX + 5, y);

    y = y+7;
    const hinweis = 'Mängelrügen sind unverzüglich, jedoch spätestens binnen 7 AT zu melden.'
    doc.setFillColor(221,221,221);
    doc.rect(leftMargin, y - 4, doc.getTextWidth(hinweis) + 3, 6, 'F');
    doc.text(hinweis, leftMargin+2, y);
    doc.setFont('Lato', 'bold');
    doc.text(`${data.processingFee} %`, rightColumnX, y, { align: 'right' });
    doc.setFont('Lato', 'normal');
    doc.text('Bearbeitungsgebühr für Warenrücknahme', rightColumnX + 5, y);

    y = y + 15;
    doc.setDrawColor(84,84,84);
    doc.line(leftMargin, y, doc.internal.pageSize.getWidth() - leftMargin, y);

    y = y + 15;
    doc.setFontSize(baseFontSize * 0.8);
    doc.setTextColor(102,102,102);
    doc.text('Erläuterungen:', leftMargin, y);
    doc.text('AT = Arbeitstage Mo - Fr; ausgenommen Feiertage', leftMargin, y + 5);
    doc.text('Bearbeitungsgebühren fallen nach Überschreitung der Rückgabefrist für Bestellfehler an.', leftMargin, y + 10);
    doc.save('Retourenbedingungen_' + data.idf + '.pdf');
  }

}
